import { Info } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Modal,
  Typography,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { THEME_COLORS } from "../../../context/constants";

export default function VariableInfo() {
  const [open, setOpen] = useState(false);

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title="How to use variables">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{ color: THEME_COLORS.yellow }}>
          <Info fontSize="small" />
        </IconButton>
      </Tooltip>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          gap={2}
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 460,
            height: 500,
            bgcolor: THEME_COLORS.white,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 1,
            }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              How to use variables
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              Variables let you insert dynamic content into your scripts or
              templates. They’re automatically replaced when generating your
              final text or video.
            </Typography>

            <Typography variant="body2" sx={{ color: THEME_COLORS.darkGray2 }}>
              ✅ <strong>Syntax:</strong> Wrap variable names in double curly
              braces:
            </Typography>

            <Box
              sx={{
                bgcolor: THEME_COLORS.lightGray,
                p: 1,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.85rem",
                mb: 1.5,
              }}>
              {"{{variableName}}"}
            </Box>

            <Typography variant="body2" sx={{ color: THEME_COLORS.darkGray2 }}>
              Example:
            </Typography>

            <Box
              sx={{
                bgcolor: THEME_COLORS.lightGray,
                p: 1,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.85rem",
                mb: 1,
              }}>
              {"Hello {{userName}}, welcome to {{projectTitle}}!"}
            </Box>

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              ➜ <strong>Result:</strong> Hello John Doe, welcome to MyProject!
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Manage Variables
            </Typography>

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              You can manage all your variables in the{" "}
              <strong>Variables</strong> section. Each variable you define here
              can be referenced inside your script using double curly braces,
              like <code>{"{{test_1}}"}</code>.
            </Typography>

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              Each value you add for a variable creates a{" "}
              <strong>different video version</strong>. For example, if{" "}
              <code>{"test_1"}</code> has 3 values, your project will generate 3
              distinct videos with those replacements.
            </Typography>

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              You can also <strong>upload values from a CSV file</strong> to
              speed up variable creation — each column corresponds to a
              variable, and each row creates one video version.
            </Typography>

            <Typography
              paragraph
              variant="body2"
              sx={{ color: THEME_COLORS.darkGray2 }}>
              When uploading multiple variables, values with the same row index
              (e.g., first line of CSV) are <strong>correlated</strong> —
              meaning they’ll be combined into the same video.
            </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" pt={2}>
            <Button
              variant="contained"
              onClick={() => setOpen(false)}
              sx={{
                backgroundColor: THEME_COLORS.yellow,
                color: THEME_COLORS.black,
                "&:hover": { backgroundColor: THEME_COLORS.darkYellow },
                fontWeight: 600,
              }}>
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
